import { create } from "zustand";
import tradeFromScratchStore, {
  TicketInstrument,
} from "./tradeFromScratchStore";
import type { IBlockEditBaseline } from "@/components/widgets/tradeFromScratch/helper/blockEditHelper";

// LQC-2941 - legs a user is building inside Trade Creation, before Save. UI-only: nothing
// reaches the database until the leg is saved (spec B5). The ticket itself lives in
// tradeFromScratchStore under the draft's id, the same tickets Leg Creation uses.
// Keyed by the Trade Creation widget's cryptoID, so two open widgets keep their own.

export interface IDraftLeg {
  id: string; // "draft-<uuid>", also the ticketId
  instrument: TicketInstrument;
  // LQC-3027 - set when the draft reopens a saved block; Save then PATCHes it.
  edit?: IBlockEditBaseline;
}

type State = {
  draftLegsByWidget: Record<string, IDraftLeg[]>;
  // Draft ids whose Security Details panel is open (LQC-2943).
  openSecurityDetailIds: string[];
  // Draft whose blocked Save is hovered, row or detail; turns its Missing line red.
  hoveredSaveDraftId: string | null;
  // LQC-2949 - what a draft's nominal was rounded from, shown in its detail.
  roundedFromByDraftId: Record<string, number | null>;
};

type Action = {
  addDraftLeg: (widgetId: string, draftLeg: IDraftLeg) => void;
  removeDraftLeg: (widgetId: string, draftId: string) => void;
  removeDraftLegsForWidget: (widgetId: string) => void;
  toggleSecurityDetails: (draftId: string) => void;
  setSecurityDetailsOpen: (draftId: string, isOpen: boolean) => void;
  setHoveredSaveDraftId: (draftId: string | null) => void;
  setRoundedFrom: (draftId: string, roundedFrom: number | null) => void;
};

const draftLegsStore = create<State & Action>((set) => ({
  draftLegsByWidget: {},
  openSecurityDetailIds: [],
  hoveredSaveDraftId: null,
  roundedFromByDraftId: {},

  addDraftLeg: (widgetId, draftLeg) =>
    set((state) => ({
      draftLegsByWidget: {
        ...state.draftLegsByWidget,
        [widgetId]: [...(state.draftLegsByWidget[widgetId] ?? []), draftLeg],
      },
    })),

  removeDraftLeg: (widgetId, draftId) =>
    set((state) => {
      const roundedFromByDraftId = { ...state.roundedFromByDraftId };
      delete roundedFromByDraftId[draftId];
      return {
        roundedFromByDraftId,
        draftLegsByWidget: {
          ...state.draftLegsByWidget,
          [widgetId]: (state.draftLegsByWidget[widgetId] ?? []).filter(
            (draftLeg) => draftLeg.id !== draftId,
          ),
        },
        openSecurityDetailIds: state.openSecurityDetailIds.filter(
          (id) => id !== draftId,
        ),
      };
    }),

  removeDraftLegsForWidget: (widgetId) =>
    set((state) => {
      const draftLegsByWidget = { ...state.draftLegsByWidget };
      delete draftLegsByWidget[widgetId];
      return { draftLegsByWidget };
    }),

  toggleSecurityDetails: (draftId) =>
    set((state) => ({
      openSecurityDetailIds: state.openSecurityDetailIds.includes(draftId)
        ? state.openSecurityDetailIds.filter((id) => id !== draftId)
        : [...state.openSecurityDetailIds, draftId],
    })),

  setSecurityDetailsOpen: (draftId, isOpen) =>
    set((state) => {
      const others = state.openSecurityDetailIds.filter((id) => id !== draftId);
      return { openSecurityDetailIds: isOpen ? [...others, draftId] : others };
    }),

  setHoveredSaveDraftId: (draftId) => set({ hoveredSaveDraftId: draftId }),

  setRoundedFrom: (draftId, roundedFrom) =>
    set((state) => ({
      roundedFromByDraftId: {
        ...state.roundedFromByDraftId,
        [draftId]: roundedFrom,
      },
    })),
}));

// Called when the Trade Creation widget is deleted, never on unmount (a tab switch also
// unmounts). Stores only, because widgetHelperFunctions imports it.
export function removeDraftLegsForWidget(widgetId: string) {
  const drafts = draftLegsStore.getState().draftLegsByWidget[widgetId] ?? [];
  drafts.forEach((draft) =>
    tradeFromScratchStore.getState().removeTickets(draft.id),
  );
  draftLegsStore.getState().removeDraftLegsForWidget(widgetId);
}

export default draftLegsStore;
